import React from "react";
import BiWazeTimingDisplay from "./BiWazeTimingDisplay";
import ControlPanel from "./ControlPanel";
import ModeSelector from "./ModeSelector";
import PlayButton from "./PlayButton";
import ProgressMeter from "./PressureGuage";
import { IonIcon } from "@ionic/react";
import { informationCircleOutline } from "ionicons/icons";
import {
  SHOW_THERAPY_CONTROLS,
  WEB_REMOTE_GRAPH_MIN_HEIGHT,
} from "../../../utils/constants";

/** Web-only shield: blocks control interaction and shows a "read-only" note. */
const ControlBlocker: React.FC<{
  showLabel?: boolean;
  compact?: boolean;
  overhangTop?: boolean;
}> = ({
  showLabel = false,
  compact = false,
  overhangTop = true, // false for Auto mode
}) => {
  const swallow = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <div
      onClickCapture={swallow}
      onMouseDownCapture={swallow}
      onMouseUpCapture={swallow}
      onTouchStartCapture={swallow}
      onTouchEndCapture={swallow}
      onPointerDownCapture={swallow}
      onContextMenu={swallow}
      style={{
        position: "absolute",
        // Overhangs the controls; bottom insets or it gets clipped.
        top: overhangTop ? "-20px" : "0px",
        left: "-8px",
        right: "-8px",
        bottom: "6px",
        zIndex: 5,
        cursor: "not-allowed",
        // Grey, not dark — the buttons under it are already faded.
        background: "rgba(120, 124, 130, 0.18)",
        border: "1px solid rgba(255, 255, 255, 0.14)",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 12px",
        overflow: "hidden",
      }}
    >
      {showLabel && (
        <div
          className={`flex items-center justify-center gap-2 rounded-lg ${
            compact ? "px-2 py-1" : "px-3 py-2"
          }`}
          style={{
            background: "rgba(0, 0, 0, 0.55)",
            border: "1px solid rgba(255, 255, 255, 0.14)",
            maxWidth: "100%",
          }}
        >
          <IonIcon
            icon={informationCircleOutline}
            style={{
              color: "#B0B4B7",
              fontSize: compact ? "14px" : "20px",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: "Figtree, sans-serif",
              fontSize: compact ? "11px" : "15px",
              lineHeight: 1.3,
              color: "#D8DCDF",
              textAlign: "center",
            }}
          >
            Therapy controls are available in the Arc Connect mobile app. This
            view is read-only.
          </span>
        </div>
      )}
    </div>
  );
};

interface RemoteContentProps {
  isAuto: boolean;
  pauseTime: number;
  isPlaying: boolean;
  isShortHeight: boolean;
  isMobile: boolean;
  isTablet: boolean;
  playButtonScale: number;
  isClearDevice?: boolean;
  onModeToggle?: () => void;
  onPauseTimeChange?: (newTime: number) => void;
  onPlayToggle: () => void;
  onPlayButtonScaleChange: (scale: number) => void;
}

const RemoteContent: React.FC<RemoteContentProps> = ({
  isAuto,
  pauseTime,
  isPlaying,
  isShortHeight,
  isMobile,
  isTablet,
  playButtonScale,
  isClearDevice,
  onModeToggle,
  onPlayToggle,
  onPlayButtonScaleChange,
}) => {
  if (isClearDevice) {
    return (
      // remote-container: so the scroll-fallback detection finds this too.
      <div
        className="w-full flex flex-col items-center justify-center remote-container"
        style={{ minHeight: "60vh", color: "#ffffff" }}
      >
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🚧</div>
        <h2
          style={{
            fontSize: "24px",
            fontWeight: 600,
            marginBottom: "8px",
            color: "#ffffff",
          }}
        >
          Coming Soon
        </h2>
        <p style={{ fontSize: "14px", color: "#9ca3af", textAlign: "center" }}>
          Remote control for BiWaze® Clear is not yet available.
        </p>
      </div>
    );
  }

  // Web + Auto: graph gets a min height; the play row shrinks first.
  const isWebGraph = !SHOW_THERAPY_CONTROLS && isAuto;
  const graphMinHeight = isShortHeight
    ? WEB_REMOTE_GRAPH_MIN_HEIGHT.short
    : WEB_REMOTE_GRAPH_MIN_HEIGHT.normal;

  return (
    <div
      className={`w-full flex flex-col px-3 ${isShortHeight ? "gap-2" : "gap-4"} remote-container`}
      style={
        // Web: can grow past 100%, scroll fallback picks up the rest.
        SHOW_THERAPY_CONTROLS
          ? { height: "100%" }
          : { minHeight: "100%", height: "auto", overflow: "visible" }
      }
    >
      {/* Info section — takes natural height */}
      <div className="flex flex-col">
        <ModeSelector
          isAuto={isAuto}
          onModeToggle={onModeToggle}
          isShortHeight={isShortHeight}
        />
        {!isAuto && (
          <div className={`${isShortHeight ? "" : "mb-4"}`}>
            <BiWazeTimingDisplay isShortHeight={isShortHeight} />
          </div>
        )}
        <div className={`${isShortHeight ? "" : "mb-4"}`}>
          <ProgressMeter />
        </div>
      </div>

      {/* Controls section — fills remaining height */}
      <div
        className="flex-1 flex flex-col items-center justify-between"
        // Web: minHeight 0 so this can shrink below content's default min.
        style={
          SHOW_THERAPY_CONTROLS
            ? { position: "relative" }
            : { position: "relative", minHeight: 0 }
        }
      >
        <div
          className={`${isAuto ? "flex-1 " : ""}flex justify-center w-full`}
          style={{ minHeight: isWebGraph ? graphMinHeight : undefined }}
        >
          <ControlPanel
            isAuto={isAuto}
            isShortHeight={isShortHeight}
            pauseTime={pauseTime}
            isMobile={isMobile}
          />
        </div>

        {/* Play button row: small min-height on web so it doesn't collapse. */}
        <div
          className={`flex justify-center w-full ${
            isShortHeight ? "pb-2" : "pb-4"
          }`}
          style={{
            position: "relative",
            minHeight: isWebGraph ? (isShortHeight ? 44 : 56) : undefined,
          }}
        >
          <PlayButton
            isPlaying={isPlaying}
            onToggle={onPlayToggle}
            isShortHeight={isShortHeight}
            isMobile={isMobile}
            isTablet={isTablet}
            scale={playButtonScale}
            onScaleChange={onPlayButtonScaleChange}
          />
          {isWebGraph && (
            <ControlBlocker
              showLabel
              compact={isShortHeight}
              overhangTop={false}
            />
          )}
        </div>

        {/* Manual: one blocker over +/- and play. */}
        {!SHOW_THERAPY_CONTROLS && !isAuto && <ControlBlocker showLabel />}
      </div>
    </div>
  );
};

export default RemoteContent;
